import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import { type LoginPayload, loginFormSchema, useLogin } from 'src/features/auth/api';
import { commonMessages, forgotPasswordMessages, loginMessages } from 'src/features/auth/utils';
import { InputWithPassword } from 'src/registry/components/inputs/InputWithPassword';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import LogInFormErrorBlock from 'src/features/auth/components/LogInFormErrorBlock';

const LoginForm = () => {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const {
        mutate: login,
        error,
        isPending,
    } = useLogin({
        onSuccess: () => {
            return navigate('/');
        },
    });

    const form = useForm<LoginPayload>({
        resolver: zodResolver(loginFormSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const onSubmit = (values: LoginPayload) => {
        login(values);
    };

    return (
        <>
            <div className="flex w-full max-w-sm flex-col items-center py-6 text-center">
                <h1 className="text-2xl font-semibold">{formatMessage(loginMessages.welcomeBack)}</h1>
                <h3 className="text-center text-gray-500">{formatMessage(loginMessages.loginMessage)}</h3>
            </div>
            <Form form={form} onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-sm">
                <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{formatMessage(commonMessages.email)}</FormLabel>
                            <FormControl>
                                <Input
                                    type="email"
                                    placeholder={formatMessage(commonMessages.emailPlaceholder)}
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage>{formatMessage(loginMessages.loginEmailTip)}</FormMessage>
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{formatMessage(commonMessages.password)}</FormLabel>
                            <FormControl>
                                <InputWithPassword type="password" placeholder="••••••••" {...field} />
                            </FormControl>
                            <FormMessage>{formatMessage(loginMessages.loginPasswordTip)}</FormMessage>
                        </FormItem>
                    )}
                />
                <Button className="w-full" type="submit" disabled={isPending || !form.formState.isValid}>
                    {formatMessage(loginMessages.login)}
                </Button>
                <LogInFormErrorBlock error={error} />
                <Link
                    to="/forgot-password"
                    className="text-primary-700 hover:text-primary-600 self-start text-sm hover:underline"
                >
                    <FormattedMessage {...forgotPasswordMessages.forgotPassword} />
                </Link>
            </Form>
        </>
    );
};

export { LoginForm };
